import type { BulkResult } from "@/domain/types/bulk";
import type { InvestmentSort, InvestmentSortField } from "@/domain/investment/types";
import type {
  InvestmentAggregates,
  InvestmentAggregatesParams,
} from "@/domain/investment/investmentList";
import type { IInvestmentRepository } from "@/application/ports/repositories";
import type { OrganizationInvestmentEventsResponse } from "@/domain/activity";
import type {
  InvestmentCreateRequest,
  InvestmentUpdateRequest,
  InvestmentWriteResponse,
  InvestmentWithDetailsResponse,
  InvestmentQueryParams,
  InvestmentExportParams,
  InvestmentPagination,
  ExtendReservationRequest,
  ExtendReservationResponse,
  SendReservationContractRequest,
  SendReservationContractResponse,
  SendFinancingContractRequest,
  SendFinancingContractResponse,
  SendCommunicationContractRequest,
  SendCommunicationContractResponse,
  RetryContractDispatchResponse,
  DownloadAeatDocumentsRequest,
} from "@/domain/investment";
import type {
  InvestmentDocumentResponse,
  DocumentDownloadResponse,
  PaginatedBlocksResponse,
  ReviewUpdateRequest,
  DocumentUpdateRequest,
} from "@/domain/documentBlocks";
import type { DocumentStatus, DocumentType } from "@/domain/documentEnums";
import type { ExportDownloadOptions, ExportPreview } from "@/domain/export";
import { downloadBlob, downloadFromSignedUrl } from "../fileDownload";
import { toInvestmentFilterQuery } from "../investmentFilterQuery";
import * as sdk from "../generated/sdk.gen";
import { fetchWithBudget } from "../sdkFetch";
import { unwrap, unwrapBlob, unwrapBlobWithFilename } from "../unwrap";

const toSortQuery = (sort: readonly InvestmentSort[] | undefined) =>
  sort && sort.length > 0
    ? sort.map((entry) => `${entry.key}:${entry.direction}`).join(",")
    : undefined;

export class InvestmentRepository implements IInvestmentRepository {
  async getInvestments(
    params?: InvestmentQueryParams,
    signal?: AbortSignal,
  ): Promise<InvestmentPagination> {
    const { sort, ...filters } = params ?? {};
    const { items, ...page } = await unwrap(
      sdk.listInvestments({
        query: { ...toInvestmentFilterQuery(filters), sort: toSortQuery(sort) },
        signal,
      }),
    );
    return { investments: items, ...page };
  }

  async getInvestmentAggregates(
    params: InvestmentAggregatesParams,
    signal?: AbortSignal,
  ): Promise<InvestmentAggregates> {
    const { aggregates, ...filters } = params;
    const response = await unwrap(
      sdk.getInvestmentAggregates({
        query: { ...toInvestmentFilterQuery(filters), aggregates: aggregates.join(",") },
        signal,
      }),
    );
    return response.aggregates;
  }

  async getInvestment(uuid: string, signal?: AbortSignal): Promise<InvestmentWithDetailsResponse> {
    return unwrap(sdk.getInvestment({ path: { investment_uuid: uuid }, signal }));
  }

  async listOrganizationEvents(
    params: { limit?: number; offset?: number },
    signal?: AbortSignal,
  ): Promise<OrganizationInvestmentEventsResponse> {
    return unwrap(sdk.listOrganizationInvestmentEvents({ query: params, signal }));
  }

  async createInvestment(data: InvestmentCreateRequest): Promise<InvestmentWriteResponse> {
    return unwrap(sdk.createInvestment({ body: data }));
  }

  async updateInvestment(
    uuid: string,
    data: InvestmentUpdateRequest,
  ): Promise<InvestmentWriteResponse> {
    return unwrap(sdk.updateInvestment({ path: { investment_uuid: uuid }, body: data }));
  }

  async bulkUpdateInvestments(
    uuids: readonly string[],
    data: InvestmentUpdateRequest,
  ): Promise<BulkResult> {
    return unwrap(sdk.bulkUpdateInvestments({ body: { investment_uuids: [...uuids], data } }));
  }

  async deleteInvestment(uuid: string): Promise<void> {
    await unwrap(sdk.deleteInvestment({ path: { investment_uuid: uuid } }));
  }

  async extendReservation(
    uuid: string,
    data: ExtendReservationRequest,
  ): Promise<ExtendReservationResponse> {
    return unwrap(sdk.extendReservation({ path: { investment_uuid: uuid }, body: data }));
  }

  async sendReservationContract(
    uuid: string,
    data: SendReservationContractRequest,
  ): Promise<SendReservationContractResponse> {
    // 202 Accepted — DocuSign runs in a background task on the server.
    // Extended timeout only covers PDF render + GCS upload + DB write.
    return unwrap(
      sdk.sendReservationContract({
        path: { investment_uuid: uuid },
        body: data,
        fetch: fetchWithBudget(30000),
      }),
    );
  }

  async sendFinancingContract(
    uuid: string,
    data: SendFinancingContractRequest,
  ): Promise<SendFinancingContractResponse> {
    return unwrap(
      sdk.sendFinancingContract({
        path: { investment_uuid: uuid },
        body: data,
        fetch: fetchWithBudget(30000),
      }),
    );
  }

  async previewReservationContract(
    uuid: string,
    data: SendReservationContractRequest,
  ): Promise<string> {
    const blob = await unwrapBlob(sdk.previewReservationContract, {
      path: { investment_uuid: uuid },
      body: data,
      fetch: fetchWithBudget(15000),
    });
    return URL.createObjectURL(blob);
  }

  async previewFinancingContract(
    uuid: string,
    data: SendFinancingContractRequest,
  ): Promise<string> {
    const blob = await unwrapBlob(sdk.previewFinancingContract, {
      path: { investment_uuid: uuid },
      body: data,
      fetch: fetchWithBudget(15000),
    });
    return URL.createObjectURL(blob);
  }

  async sendCommunicationContract(
    uuid: string,
    data: SendCommunicationContractRequest,
  ): Promise<SendCommunicationContractResponse> {
    return unwrap(
      sdk.sendCommunicationContract({
        path: { investment_uuid: uuid },
        body: data,
        fetch: fetchWithBudget(60000),
      }),
    );
  }

  async previewCommunicationContract(
    uuid: string,
    data: SendCommunicationContractRequest,
  ): Promise<string> {
    const blob = await unwrapBlob(sdk.previewCommunicationContract, {
      path: { investment_uuid: uuid },
      body: data,
      fetch: fetchWithBudget(30000),
    });
    return URL.createObjectURL(blob);
  }

  async retryContractDispatch(
    investmentUuid: string,
    documentUuid: string,
  ): Promise<RetryContractDispatchResponse> {
    return unwrap(
      sdk.retryContractDispatch({
        path: { investment_uuid: investmentUuid, document_uuid: documentUuid },
        fetch: fetchWithBudget(15000),
      }),
    );
  }

  async listDocuments(
    investmentUuid: string,
    params?: {
      document_type?: DocumentType;
      status?: DocumentStatus;
      search?: string;
      limit?: number;
      offset?: number;
    },
    signal?: AbortSignal,
  ): Promise<PaginatedBlocksResponse<InvestmentDocumentResponse>> {
    return unwrap(
      sdk.listInvestmentDocuments({
        path: { investment_uuid: investmentUuid },
        query: params,
        signal,
      }),
    );
  }

  async uploadDocument(
    uuid: string,
    file: File,
    documentType: DocumentType,
    title?: string,
  ): Promise<InvestmentDocumentResponse> {
    return unwrap(
      sdk.uploadInvestmentDocument({
        path: { investment_uuid: uuid },
        query: { document_type: documentType, title },
        body: { document: file },
        fetch: fetchWithBudget(60000),
      }),
    );
  }

  async downloadDocument(investmentUuid: string, documentUuid: string): Promise<void> {
    const docInfo = await this.getDocumentDownloadUrl(investmentUuid, documentUuid);
    await downloadFromSignedUrl(
      docInfo.signed_url,
      docInfo.filename || `investment-document-${documentUuid}.pdf`,
    );
  }

  async reviewDocument(
    investmentUuid: string,
    documentUuid: string,
    review: ReviewUpdateRequest,
  ): Promise<InvestmentDocumentResponse> {
    return unwrap(
      sdk.reviewInvestmentDocument({
        path: { investment_uuid: investmentUuid, document_uuid: documentUuid },
        body: review,
      }),
    );
  }

  async updateDocument(
    investmentUuid: string,
    documentUuid: string,
    data: DocumentUpdateRequest,
  ): Promise<InvestmentDocumentResponse> {
    return unwrap(
      sdk.updateInvestmentDocument({
        path: { investment_uuid: investmentUuid, document_uuid: documentUuid },
        body: data,
      }),
    );
  }

  async deleteDocument(investmentUuid: string, documentUuid: string): Promise<void> {
    await unwrap(
      sdk.deleteInvestmentDocument({
        path: { investment_uuid: investmentUuid, document_uuid: documentUuid },
      }),
    );
  }

  async getDocumentDownloadUrl(
    investmentUuid: string,
    documentUuid: string,
  ): Promise<DocumentDownloadResponse> {
    return unwrap(
      sdk.downloadInvestmentDocument({
        path: { investment_uuid: investmentUuid, document_uuid: documentUuid },
      }),
    );
  }

  async previewExport(
    params: InvestmentExportParams,
    signal?: AbortSignal,
  ): Promise<ExportPreview> {
    const { sort: _sort, ...filters } = params;
    return unwrap(
      sdk.previewInvestmentsExport({
        query: toInvestmentFilterQuery(filters),
        signal,
      }),
    );
  }

  async downloadExport(
    params: InvestmentExportParams,
    options: ExportDownloadOptions,
  ): Promise<void> {
    const { sort, ...filters } = params;
    const { blob, filename } = await unwrapBlobWithFilename(
      sdk.exportInvestments,
      {
        query: {
          ...toInvestmentFilterQuery(filters),
          format: options.format,
          columns: options.columns.length > 0 ? options.columns.join(",") : undefined,
          sort: toSortQuery(sort),
          sort_by: (options.sort_by as InvestmentSortField | undefined) ?? params.sort_by,
          sort_direction: options.sort_direction ?? params.sort_direction,
        },
      },
      `investments.${options.format}`,
    );
    downloadBlob(blob, filename);
  }

  async downloadAeatDocuments(uuid: string, data: DownloadAeatDocumentsRequest): Promise<void> {
    const { blob, filename } = await unwrapBlobWithFilename(
      sdk.downloadAeatDocuments,
      {
        path: { investment_uuid: uuid },
        body: data,
        fetch: fetchWithBudget(30000),
      },
      `aeat_documents_${uuid}.zip`,
    );
    downloadBlob(blob, filename);
  }
}
